// The rendering policy for assistant HTML.
//
// THIS IS THE SECURITY BOUNDARY. The backend also strips disallowed images, but
// that pass is quality control: assistant output is shaped by student input, so
// it is untrusted no matter what the server did to it. Everything here is
// enforced again at render time.
//
// Kept as data, separate from the renderer, so the policy can be tested and
// reasoned about on its own.

// Tags an assistant reply may use. Deliberately excludes h1/h2 (the panel owns
// its heading level), and every embedding, scripting, and form element.
export const CHAT_ALLOWED_TAGS = [
  'a',
  'abbr',
  'blockquote',
  'br',
  'caption',
  'code',
  'dd',
  'details',
  'div',
  'dl',
  'dt',
  'em',
  'figcaption',
  'figure',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'img',
  'kbd',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  's',
  'section',
  'span',
  'strong',
  'sub',
  'summary',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];

// Attributes an assistant reply may set.
//
// Notable exclusions: `style` (an overlay and background-image vector), `id`
// (would collide with the host page), `target`/`srcset`, and every `on*`
// handler. DOMPurify removes anything not listed.
export const CHAT_ALLOWED_ATTRS = [
  'alt',
  'class',
  'colspan',
  'dir',
  'href',
  'lang',
  'open',
  'rowspan',
  'scope',
  'src',
  'title',
];

// The Kudu class vocabulary.
//
// This is what makes rich output *beautiful* without letting the model write
// CSS: Kudu styles these classes responsively and on-brand, and the model
// composes them. The list is documented to the model in the prompt contract and
// must stay in sync with chapterChatHtmlContract on the backend and with
// ChatHtmlRenderer.module.css.
export const CHAT_ALLOWED_CLASS_NAMES = new Set([
  'kudu-callout',
  'kudu-callout-warning',
  'kudu-card',
  'kudu-compare',
  'kudu-figure',
  'kudu-grid-2',
  'kudu-key-term',
  'kudu-muted',
  'kudu-steps',
]);

// Image sources an assistant reply may render.
//
// Chapter figures are matched by EXACT URL against the set the backend injected
// for this chapter — host matching would be far too permissive, because Kudu
// images are served from shared Google infrastructure that also serves every
// other Google-hosted image.
const ALLOWED_IMAGE_HOSTS = new Set([
  'assets.openstax.org',
  'cnx.org',
  'commons.wikimedia.org',
  'en.wikipedia.org',
  'openstax.org',
  'upload.wikimedia.org',
  'wikimedia.org',
  'wikipedia.org',
]);

export function isAllowedImageSrc(
  src: string,
  chapterImageUrls: ReadonlySet<string> = new Set(),
): boolean {
  const trimmed = src.trim();

  if (!trimmed) {
    return false;
  }

  if (chapterImageUrls.has(trimmed)) {
    return true;
  }

  try {
    const url = new URL(trimmed);
    // https only: an http image is blocked as mixed content anyway, and allowing
    // it invites a downgrade.
    return url.protocol === 'https:' && ALLOWED_IMAGE_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    // Relative or malformed URLs are never allowed — a bare path would resolve
    // against Kudu's own origin.
    return false;
  }
}

// Links may only leave for the open web over http(s). Anything else — most
// importantly `javascript:` — is dropped.
export function isAllowedLinkHref(href: string): boolean {
  try {
    const url = new URL(href.trim(), window.location.origin);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function filterClassNames(value: string): string[] {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .filter((className) => CHAT_ALLOWED_CLASS_NAMES.has(className));
}
